<script setup lang="ts">
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import {
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { AppLinkButton, AppRouterLink } from '@libs/ui';
import { exhaustiveGuard } from '@libs/utils';

import { ItinerarySegmentType, UnavailabilityReason } from '../../models';

interface AccommodationUnavailableProductAction {
  reason: UnavailabilityReason;
  segmentType: ItinerarySegmentType.Accommodation;
  ingredientIndex: number;
}

interface ActivityUnavailableProductAction {
  reason: UnavailabilityReason;
  segmentType: ItinerarySegmentType.Activity;
  ingredientIndex: number;
}

interface TransportUnavailableProductAction {
  reason: UnavailabilityReason;
  segmentType: ItinerarySegmentType.Ferry | ItinerarySegmentType.Flight;
}

type ProductAction =
  | AccommodationUnavailableProductAction
  | ActivityUnavailableProductAction
  | TransportUnavailableProductAction;

interface UnavailableProductActionProps {
  productAction: ProductAction;
}
const props = defineProps<UnavailableProductActionProps>();

interface UnavailableProductActionEmits {
  (event: 'try-another-date'): void;
  (event: 'edit-filters'): void;
}
const emit = defineEmits<UnavailableProductActionEmits>();

const statsigEventsStore = useStatsigEventsStore();

const { t } = i18n.global;
const routerStore = useRouterStore();

const actionText = computed<string>(() =>
  getActionText(props.productAction.reason, props.productAction.segmentType)
);

function getActionText(
  reason: UnavailabilityReason,
  segmentType: ItinerarySegmentType
): string {
  switch (reason) {
    case UnavailabilityReason.SelectedDate:
      return t('planner.no_availability.actions.try_another_date');

    case UnavailabilityReason.SelectedFilters:
      return t('planner.no_availability.actions.edit_your_filters');

    case UnavailabilityReason.SelectedProduct: {
      if (segmentType === ItinerarySegmentType.Accommodation) {
        return t('planner.no_availability.actions.explore_alternative_hotels');
      }

      if (segmentType === ItinerarySegmentType.Activity) {
        return t(
          'planner.no_availability.actions.explore_alternative_activities'
        );
      }

      if (segmentType === ItinerarySegmentType.Ferry) {
        return t('planner.no_availability.actions.explore_alternative_ferries');
      }

      if (segmentType === ItinerarySegmentType.Flight) {
        return t('planner.no_availability.actions.explore_alternative_flights');
      }

      throw new Error('Unexpected segment type on UnavailableProductAction');
    }

    case UnavailabilityReason.SuggestedFromHotelCodeSearch:
      return t('planner.no_availability.actions.explore_alternative_hotels');

    case UnavailabilityReason.Suggested:
      return t('filter.edit_filters');
  }
}

function handleAction(reason: UnavailabilityReason): void {
  switch (reason) {
    case UnavailabilityReason.SelectedDate:
      emit('try-another-date');
      statsigEventsStore.logTripOverviewUnavailableProductActionClicked({
        actionType: 'changeDate',
      });
      break;

    case UnavailabilityReason.SelectedFilters:
      emit('edit-filters');
      statsigEventsStore.logTripOverviewUnavailableProductActionClicked({
        actionType: 'changeFilters',
      });
      break;

    case UnavailabilityReason.SelectedProduct:
    case UnavailabilityReason.SuggestedFromHotelCodeSearch:
      statsigEventsStore.logTripOverviewUnavailableProductActionClicked({
        actionType: 'changeProduct',
      });
      break;

    case UnavailabilityReason.Suggested:
      emit('edit-filters');
      statsigEventsStore.logTripOverviewUnavailableProductActionClicked({
        actionType: 'changeFilters',
      });
      break;

    default:
      exhaustiveGuard(reason, 'Unexpected UnavailabilityReason');
  }
}
const link = computed<string>(() => {
  const segmentType = props.productAction.segmentType;

  switch (segmentType) {
    case ItinerarySegmentType.Accommodation:
      return routerStore.getHotelSelectionUrl({
        ingredientIndex: props.productAction.ingredientIndex,
      });
    case ItinerarySegmentType.Flight:
      return routerStore.getFlightListUrl();
    case ItinerarySegmentType.Ferry:
      return routerStore.getFerryListUrl();
    case ItinerarySegmentType.Activity:
      return routerStore.getActivitySelectionUrl(
        props.productAction.ingredientIndex
      );
    default:
      throw new Error('Impossible case!');
  }
});
</script>

<template>
  <AppLinkButton
    v-if="props.productAction.reason !== UnavailabilityReason.SelectedProduct"
    :text="actionText"
    size="s"
    class="unavailable-product-action-button-link"
    @click="handleAction(props.productAction.reason)"
  />
  <AppRouterLink
    v-else
    :link="link"
    font="font-link-s-light"
    color="color-neutral-70"
    class="unavailable-product-action-router-link"
    @navigation-triggered="handleAction(props.productAction.reason)"
  >
    <span class="unavailable-product-action-link">
      {{ actionText }}
    </span>
  </AppRouterLink>
</template>

<style scoped lang="scss">
.unavailable-product-action-link {
  text-decoration: underline;
  text-align: center;
}

.unavailable-product-action-button-link,
.unavailable-product-action-router-link {
  text-align: center;
}
</style>
